/**
 * Quotes (Preventivatore, proposal §4): the cycle of the document, which does
 * not depend on how the price is worked out. In this first release the lines
 * are written by hand; the calculation on the price list comes with its own
 * discussion and will produce the same lines.
 *
 * Totals are pure and work on whole cents (money.ts), so what the user adds up
 * on the screen is what the database holds.
 */
import { z } from 'zod';
import { formatMoney, fromCents, parseDecimal, toCents, toHundredths, toThousandths, } from './money.js';
import { entryCodeSchema } from './reference-tables.js';
import { idSchema, isoDateSchema, optionalText } from './validation.js';
/**
 * States of a quote (rule of 03/10/2026): the **preventivo** is internal and
 * weighs costs and revenues; once approved it becomes an **offerta**, the
 * document for the client, numbered OFF-AAAA-NNNN with its version. A change
 * asked by the client is a new version: the preventivo opens again, is
 * approved again, and the previous version is «superata».
 */
export const QUOTE_STATES = [
    'bozza',
    'da_inviare',
    'inviata',
    'accettata',
    'rifiutata',
    'annullata',
    'superata',
];
export const QUOTE_STATE_LABELS = {
    bozza: 'Preventivo in bozza',
    da_inviare: 'Da inviare',
    inviata: 'Inviata',
    accettata: 'Accettata',
    rifiutata: 'Rifiutata',
    annullata: 'Annullata',
    superata: 'Superata',
};
/**
 * The states written before 03/10/2026, still allowed in the database until a
 * cleanup release converts them: an issued quote reads as an offer sent.
 */
const LEGACY_STATES = {
    emesso: 'inviata',
    accettato: 'accettata',
    rifiutato: 'rifiutata',
    scaduto: 'inviata',
    annullato: 'annullata',
    sostituito: 'superata',
};
/** The state of a row, legacy values read as the new ones. */
export function quoteStateOf(stored) {
    return QUOTE_STATES.includes(stored)
        ? stored
        : (LEGACY_STATES[stored] ?? 'annullata');
}
/** The stored values that mean a state, legacy ones included: for the queries. */
export function storedStates(state) {
    return [
        state,
        ...Object.entries(LEGACY_STATES)
            .filter(([, value]) => value === state)
            .map(([key]) => key),
    ];
}
export function phaseOf(state) {
    return state === 'bozza' ? 'preventivo' : 'offerta';
}
/** Where an offer can go by hand from each state; «scaduta» comes by itself. */
const OFFER_MOVES = {
    da_inviare: ['inviata', 'accettata', 'rifiutata', 'annullata'],
    inviata: ['accettata', 'rifiutata', 'annullata'],
};
export function nextOfferStates(state) {
    return OFFER_MOVES[state] ?? [];
}
/** States an offer can be moved to by hand. */
export const OFFER_MOVE_STATES = ['inviata', 'accettata', 'rifiutata', 'annullata'];
/** An offer still open with the client: it can be sent, accepted or get a new version. */
export function isOpenOffer(state) {
    return state === 'da_inviare' || state === 'inviata';
}
/** An open offer past its validity: shown as «scaduta» (decision of 03/10/2026). */
export function isExpired(quote, today) {
    return isOpenOffer(quote.state) && quote.validUntil !== null && quote.validUntil < today;
}
/** A quote is edited only while it is a preventivo in draft. */
export function isEditable(state) {
    return state === 'bozza';
}
/**
 * The conditions (payment, instalments, billing, dates, validity, notes for
 * the client) change on the preventivo and also on the offer until it is sent
 * (decision of 05/10/2026): the client has not received it yet. After that a
 * change is a new version.
 */
export function conditionsEditable(state) {
    return state === 'bozza' || state === 'da_inviare';
}
/** The fields of a quote that are conditions: the only ones an offer still to send takes. */
export const OFFER_CONDITION_FIELDS = [
    'paymentTermsCode',
    'billingNotes',
    'startDate',
    'endDate',
    'penalties',
    'validityDays',
    'clientNotes',
    'projectManagerId',
    'adminContactId',
];
/**
 * The references in K-Rev printed on the offer (2.58): not its content, so
 * changing them keeps the validation of the CFO.
 */
export const OFFER_CONTACT_FIELDS = ['projectManagerId', 'adminContactId'];
/** The first-level voices, each with its sub-types, in the order of the table. */
export function typeTree(types) {
    return types
        .filter((type) => !type.parent)
        .map((type) => ({
        type,
        children: types.filter((child) => child.parent === type.code),
    }));
}
/**
 * What the client reads on the line: the chosen voices, then the free text.
 * «Formazione — Altro: Workshop sicurezza», «Consulenza — Task Force».
 */
export function lineLabel(parts) {
    const chosen = [parts.type, parts.subType].filter(Boolean).join(' — ');
    const text = parts.description?.trim();
    if (!chosen)
        return text ?? '';
    return text ? `${chosen}: ${text}` : chosen;
}
/** Discount on the whole document: the taxable amount or the total the user wants. */
export const DOCUMENT_DISCOUNT_KINDS = ['net', 'total'];
/** A line in cents: its amount before and after its own discount. */
function lineCents(line, rates) {
    const quantity = toThousandths(line.quantity) * (line.editions ?? 1);
    const price = toCents(line.unitPrice);
    const exact = (quantity * price) / 1000;
    const premium = Math.min(toCents(line.premium ?? 0), Math.round(exact));
    const final = line.finalAmount === null || line.finalAmount === undefined ? null : line.finalAmount;
    let net;
    if (final !== null && String(final).trim() !== '') {
        net = toCents(final);
    }
    else {
        // The percentage falls only on what can be discounted: never on the premium.
        const discount = toHundredths(line.discountPercent ?? 0);
        net = premium + Math.round(((exact - premium) * (10_000 - discount)) / 10_000);
    }
    return {
        gross: Math.round(exact),
        premium,
        net,
        rate: rates[line.vatCode] ?? 0,
        vatCode: line.vatCode,
    };
}
/**
 * Splits `amount` cents in proportion to `weights`, to the cent: whatever the
 * rounding leaves goes to the largest remainders, so the parts always add up.
 */
export function splitCents(amount, weights) {
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    if (amount === 0 || total <= 0)
        return weights.map(() => 0);
    const exact = weights.map((weight) => (amount * weight) / total);
    const parts = exact.map((value) => Math.floor(value));
    let left = amount - parts.reduce((sum, part) => sum + part, 0);
    const order = exact
        .map((value, index) => ({ index, rest: value - Math.floor(value), weight: weights[index] }))
        .sort((a, b) => b.rest - a.rest || b.weight - a.weight || a.index - b.index);
    for (const { index } of order) {
        if (left <= 0)
            break;
        parts[index] += 1;
        left -= 1;
    }
    return parts;
}
/** The document with `discount` cents off, spread on what each line can still lose. */
function outcome(lines, discount) {
    const shares = splitCents(discount, lines.map((line) => Math.max(0, line.net - line.premium)));
    const taxable = new Map();
    lines.forEach((line, index) => {
        taxable.set(line.vatCode, (taxable.get(line.vatCode) ?? 0) + line.net - shares[index]);
    });
    // The VAT of the quote is worked out on the taxable amount of each rate, not
    // by adding up the lines: that is what an invoice does.
    const vatByRate = [...taxable].map(([code, amount]) => {
        const rate = lines.find((line) => line.vatCode === code)?.rate ?? 0;
        return { code, rate, taxable: amount, vat: Math.round((amount * rate) / 100) };
    });
    const net = vatByRate.reduce((sum, item) => sum + item.taxable, 0);
    const vat = vatByRate.reduce((sum, item) => sum + item.vat, 0);
    return { shares, taxable, vatByRate, net, vat, total: net + vat };
}
/**
 * The discount in cents that brings the document to `target`, the lowest one
 * whose result does not go over it: the target itself when reachable, else the
 * nearest amount below it (a total can miss by a cent because of the VAT).
 */
function discountFor(lines, target) {
    const linesNet = lines.reduce((sum, line) => sum + line.net, 0);
    const room = lines.reduce((sum, line) => sum + Math.max(0, line.net - line.premium), 0);
    if (target.kind === 'net')
        return Math.min(room, Math.max(0, linesNet - target.cents));
    const resultOf = (discount) => outcome(lines, discount).total;
    if (resultOf(0) <= target.cents)
        return 0;
    if (resultOf(room) > target.cents)
        return room;
    let low = 0;
    let high = room;
    while (high - low > 1) {
        const middle = Math.floor((low + high) / 2);
        if (resultOf(middle) > target.cents)
            low = middle;
        else
            high = middle;
    }
    // Spreading on several rates the total does not always move a cent at a
    // time: look around for a discount that hits the target exactly.
    for (let discount = Math.max(0, high - 10); discount <= Math.min(room, high + 10); discount++) {
        if (resultOf(discount) === target.cents)
            return discount;
    }
    return high;
}
/**
 * Totals of a quote. `rates` gives the percentage of every VAT code (from the
 * table Aliquote IVA); an unknown code counts as 0%. `target` is the taxable
 * amount or the total the user set on the document: the difference becomes a
 * discount, spread on the lines in proportion to what they can lose, never on
 * the premium (decision of 26/09/2026).
 */
export function quoteTotals(lines, rates = {}, target = null) {
    const cents = lines.map((line) => lineCents(line, rates));
    const linesNet = cents.reduce((sum, line) => sum + line.net, 0);
    const targetCents = target ? { kind: target.kind, cents: toCents(target.amount) } : null;
    const discount = targetCents ? discountFor(cents, targetCents) : 0;
    const result = outcome(cents, discount);
    return {
        lines: cents.map((line, index) => {
            const sold = line.net - result.shares[index];
            return {
                gross: fromCents(line.gross),
                net: fromCents(line.net),
                documentDiscount: fromCents(result.shares[index]),
                sold: fromCents(sold),
                vat: fromCents(Math.round((sold * line.rate) / 100)),
            };
        }),
        linesNet: fromCents(linesNet),
        documentDiscount: fromCents(discount),
        net: fromCents(result.net),
        vatByRate: result.vatByRate.map((item) => ({
            code: item.code,
            rate: item.rate,
            taxable: fromCents(item.taxable),
            vat: fromCents(item.vat),
        })),
        vat: fromCents(result.vat),
        total: fromCents(result.total),
        target: targetCents
            ? {
                kind: targetCents.kind,
                amount: fromCents(targetCents.cents),
                exact: (targetCents.kind === 'net' ? result.net : result.total) === targetCents.cents,
            }
            : null,
    };
}
/**
 * Why the discount of a line cannot be taken, or null: the amount written by
 * hand can only lower the line (for now), and never below its premium.
 */
export function lineDiscountError(line) {
    const final = line.finalAmount;
    if (final === null || final === undefined || String(final).trim() === '')
        return null;
    const { gross, premium } = lineCents(line, {});
    const cents = toCents(final);
    if (cents > gross) {
        return `L’importo finale non può superare l’importo della riga (${formatMoney(fromCents(gross))})`;
    }
    if (cents < premium) {
        return `L’importo finale non può scendere sotto il premium, che non si sconta (${formatMoney(fromCents(premium))})`;
    }
    return null;
}
/**
 * Why the document cannot reach `target`, or null: it can only lower the
 * document, and never so far as to discount the premium.
 */
export function documentDiscountError(lines, rates, target) {
    const cents = lines.map((line) => lineCents(line, rates));
    if (cents.length === 0)
        return 'Aggiungi prima le righe del preventivo';
    const room = cents.reduce((sum, line) => sum + Math.max(0, line.net - line.premium), 0);
    const highest = outcome(cents, 0);
    const lowest = outcome(cents, room);
    const asked = toCents(target.amount);
    const [max, min, what] = target.kind === 'net'
        ? [highest.net, lowest.net, 'L’imponibile']
        : [highest.total, lowest.total, 'Il totale'];
    if (asked > max)
        return `${what} non può superare ${formatMoney(fromCents(max))}`;
    if (asked < min) {
        return `${what} non può scendere sotto ${formatMoney(fromCents(min))}: il premium delle licenze non si sconta`;
    }
    return null;
}
/**
 * Voice of the table Tipologie di riga of the licence lines: they come only
 * from the licence configurator, never typed by hand (decision of 26/09/2026).
 */
export const LICENCE_TYPE_CODE = 'LIC';
/** Number of an offer: OFF-2026-0001, without gaps; every version keeps it. */
export function offerNumber(year, progressive) {
    return `OFF-${year}-${String(progressive).padStart(4, '0')}`;
}
/** How an offer is cited: «OFF-2026-0001 v2». */
export function offerLabel(number, version) {
    return version ? `${number} v${version}` : number;
}
/* --- Requests ------------------------------------------------------------------ */
const money = z
    .union([z.string(), z.number()])
    .transform((value) => fromCents(toCents(value)))
    .catch(() => {
    throw new Error('Importo non valido');
});
const percentValue = z
    .union([z.string(), z.number()])
    .transform((value) => (toHundredths(value) / 100).toFixed(2))
    .refine((value) => Number(value) >= 0 && Number(value) <= 100, {
    message: 'Sconto tra 0 e 100',
});
const percent = z.union([z.string(), z.number()]).default(0).pipe(percentValue);
const optionalMoney = z
    .union([z.string(), z.number()])
    .nullish()
    .transform((value) => value === null || value === undefined || String(value).trim() === ''
    ? null
    : fromCents(toCents(value)))
    .catch(() => {
    throw new Error('Importo non valido');
})
    .refine((value) => value === null || Number(value) >= 0, {
    message: 'L’importo non può essere negativo',
});
/** A quantity with three decimals, greater than zero. */
const quantity = z
    .union([z.string(), z.number()])
    .transform((value) => (toThousandths(value) / 1000).toFixed(3))
    .refine((value) => Number(value) > 0, { message: 'La quantità deve essere maggiore di zero' });
/** How many times a line is repeated (2.49): a whole number, at least 1. */
export const editionsSchema = z.coerce
    .number({ message: 'Edizioni non valide' })
    .int({ message: 'Le edizioni sono un numero intero' })
    .min(1, { message: 'Almeno una edizione' })
    .max(999, { message: 'Al massimo 999 edizioni' });
/** Discount of one licence line, chosen in the configurator. */
const licenceLineDiscount = z.object({
    discountPercent: percent,
    finalAmount: optionalMoney,
});
export const quoteLineSchema = z.object({
    /** Voice of the table Tipologie di riga: what is being sold. */
    typeCode: entryCodeSchema,
    /** Sub-type of that voice, when it has any. */
    subTypeCode: z
        .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
        .transform((value) => value ?? null),
    /** Free text, required by the voices «Altro» and added to the chosen ones. */
    description: optionalText(300),
    /** Code of the unit of measure (table Unità di misura). */
    unitCode: entryCodeSchema,
    /** How many times the line is repeated (2.49): the amount is editions × quantity × price. */
    editions: editionsSchema.default(1),
    quantity,
    unitPrice: money,
    discountPercent: percent,
    /**
     * Amount of the line written by hand, instead of the percentage; null
     * without one. It can only lower the line (decision of 26/09/2026).
     */
    finalAmount: optionalMoney,
    vatCode: entryCodeSchema,
    /** How often or over what period, as the offer says it: «una tantum», «12 mesi»… */
    recurrence: optionalText(120),
    /** A spesa (2.52, 2.53): trasferte, vitto, rimborsi; out of the margin, checked on its own. */
    isExpense: z.boolean().default(false),
});
/**
 * The price lists to work on (proposal Listini §5): empty for the Base. Used
 * by `GET /api/quotes/:id/lists/preview` (query) and `POST /api/quotes/:id/lists`.
 */
const listChoice = {
    activityListCode: z
        .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
        .transform((value) => value ?? null),
    costListCode: z
        .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
        .transform((value) => value ?? null),
};
export const listsPreviewSchema = z.object(listChoice);
/** Body of `POST /api/quotes/:id/lists`: the new lists and the lines and costs to price again. */
export const changeListsSchema = z.object({
    ...listChoice,
    lines: z.array(z.string().uuid()).max(500).default([]),
    costs: z.array(z.string().uuid()).max(500).default([]),
});
/** Body of `PATCH /api/quotes/:id/lines/:lineId`: only the fields to change. */
export const updateQuoteLineSchema = quoteLineSchema
    // Without the default: a change that does not name the discount leaves it alone.
    .extend({ discountPercent: percentValue, editions: editionsSchema, isExpense: z.boolean() })
    .partial()
    .refine((body) => Object.keys(body).length > 0, { message: 'Nessuna modifica' });
/** Body of `POST /api/quotes/:id/lines/reorder`: the lines in the order they must keep. */
export const reorderLinesSchema = z.object({
    lines: z.array(idSchema).max(200, { message: 'Al massimo 200 righe' }),
});
const quoteFields = {
    /** Client of the quote: a subject with an active Cliente role. */
    clientId: idSchema.nullish().transform((value) => value ?? null),
    /** Free name while the quote has no client yet (proposal §4). */
    referenceName: optionalText(200),
    referentCode: z
        .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
        .transform((value) => value ?? null),
    notes: optionalText(2000),
    /** Days the offer stays valid after it is approved: 30 unless changed (03/10/2026). */
    validityDays: z.coerce.number().int().min(1).max(365),
    /** Payment terms proposed to the client (table Termini di pagamento). */
    paymentTermsCode: z
        .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
        .transform((value) => value ?? null),
    /** How the offer will be invoiced, in words, until the billing plan of the job. */
    billingNotes: optionalText(1000),
    /** Penalties or clauses that are not standard: one reason for a complex contract. */
    penalties: z.boolean(),
    /** Notes for the client, printed in the offer under its lines (2.58). */
    clientNotes: optionalText(2000),
    /** The project manager the client finds on the offer (2.58): an active user. */
    projectManagerId: idSchema.nullish().transform((value) => value ?? null),
    /** The administrative reference on the offer: from Parametri, changeable (2.58). */
    adminContactId: idSchema.nullish().transform((value) => value ?? null),
    /** When the work starts and ends. */
    startDate: z
        .preprocess((value) => (value === '' ? null : value), isoDateSchema.nullish())
        .transform((value) => value ?? null),
    endDate: z
        .preprocess((value) => (value === '' ? null : value), isoDateSchema.nullish())
        .transform((value) => value ?? null),
};
const datesInOrder = (body) => !body.startDate || !body.endDate || body.startDate <= body.endDate;
const datesMessage = { message: 'La fine viene dopo l’inizio', path: ['endDate'] };
export const createQuoteSchema = z
    .object({
    ...quoteFields,
    // The validity and the payment terms come from the Parametri when not sent.
    validityDays: quoteFields.validityDays.optional(),
    penalties: quoteFields.penalties.default(false),
})
    .refine(datesInOrder, datesMessage);
/** Only what is sent changes: no default fills a field left out (a change of the notes kept the validity). */
export const updateQuoteSchema = z
    .object(quoteFields)
    .partial()
    .refine((body) => Object.keys(body).length > 0, { message: 'Nessuna modifica' })
    .refine(datesInOrder, datesMessage);
/** Body of `POST /api/quotes/:id/licences`: quante licenze e a quali condizioni. */
export const licenceLinesSchema = z.object({
    pax: z.coerce.number().int().min(1, { message: 'Scrivi quante licenze servono' }).max(1_000_000),
    /** Il setup si paga solo la prima volta. */
    withSetup: z.boolean().default(true),
    /** Percentuale di sconto sulle licenze del secondo anno; null senza rinnovo. */
    renewalDiscount: z
        .union([z.string(), z.number()])
        .nullish()
        .transform((value) => (value === '' || value === undefined ? null : value)),
    /** Durata del primo periodo (2.29): da 1 a 12 mesi; il rinnovo resta di 12. */
    months: z.coerce
        .number()
        .int()
        .min(1, { message: 'Da 1 a 12 mesi' })
        .max(12, { message: 'Da 1 a 12 mesi' })
        .default(12),
    /** Setup avanzato (3.11): free text and amount, a line after the setup; only with the setup. */
    extraSetup: z
        .object({
        description: z.string().trim().min(1, { message: 'Scrivi che cosa comprende' }).max(200),
        amount: z
            .string()
            .trim()
            .regex(/^\d{1,12}([.,]\d{1,2})?$/, { message: 'Importo non valido' })
            .transform((value) => fromCents(Math.round(Number(value.replace(',', '.')) * 100)))
            .refine((value) => toCents(value) > 0, { message: 'Importo non valido' }),
    })
        .nullish()
        .transform((value) => value ?? null),
    /** Discount of each line worked out, by its key: the premium is never discounted. */
    lineDiscounts: z
        .object({
        setup: licenceLineDiscount.optional(),
        setup_extra: licenceLineDiscount.optional(),
        licences: licenceLineDiscount.optional(),
        renewal: licenceLineDiscount.optional(),
    })
        .default({}),
});
const costDescription = z
    .string()
    .trim()
    .min(1, { message: 'Scrivi che cosa è il costo' })
    .max(200, { message: 'Al massimo 200 caratteri' });
const unitCost = money.refine((value) => Number(value) >= 0, {
    message: 'Il costo non può essere negativo',
});
/**
 * An estimated cost (decisions of 26 and 28/09/2026): one of the table Costi, by
 * its code, which brings its unit and unit cost, or one written by hand
 * («Personalizzato»), zero included. The quantity is its own: 10 days sold may
 * take 6 days of a senior and 10 of a junior. A voice of the table may take
 * its own description and unit cost for this quote and stays tied to its
 * voice (decision of 29/09/2026).
 */
export const plannedCostSchema = z
    .object({
    quantity,
    /** A spesa (2.53): out of the margin; left out of a change, it stays as it was. */
    isExpense: z.boolean().optional(),
})
    .and(z.union([
    z.object({
        code: entryCodeSchema,
        description: costDescription.optional(),
        unitCost: unitCost.optional(),
        /** The percentage of a fee, when the quote changes it or the voice leaves it open. */
        percent: z
            .union([z.string(), z.number()])
            .transform((value) => String(value).trim().replace(',', '.'))
            .refine((value) => /^\d{1,3}(\.\d{1,3})?$/.test(value) && Number(value) <= 100, {
            message: 'Percentuale tra 0 e 100',
        })
            .optional(),
    }),
    z.object({ description: costDescription, unitCode: entryCodeSchema, unitCost }),
]));
/**
 * Body of `POST /api/quotes/:id/costs`: a cost of a line sold, or a
 * transversal one (fees, travels) without a line, which counts in the total only.
 */
export const newPlannedCostSchema = z
    .object({ lineId: z.string().uuid().nullable() })
    .and(plannedCostSchema);
const sectionName = z
    .string()
    .trim()
    .min(1, { message: 'Scrivi il nome della sezione' })
    .max(120, { message: 'Al massimo 120 caratteri' });
/**
 * A row of «Compila da listino» (proposal Listini §7): a sub-type of the type
 * being filled, with its price and, for who sees the costs, its estimated cost
 * from the listino passività (a voice by its code, or written by hand).
 */
export const sectionRowSchema = z.object({
    subTypeCode: entryCodeSchema,
    /** Free text: required by the voices «Altro», added to the others. */
    description: optionalText(300),
    unitCode: entryCodeSchema,
    quantity,
    unitPrice: money,
    discountPercent: percent,
    cost: z
        .object({ code: entryCodeSchema.nullable(), unitCost })
        .nullish()
        .transform((value) => value ?? null),
});
/** Body of `POST /api/quotes/:id/sections`: the rows filled become a named section of the body. */
export const newSectionSchema = z.object({
    name: sectionName,
    typeCode: entryCodeSchema,
    rows: z
        .array(sectionRowSchema)
        .min(1, { message: 'Compila almeno una riga con la quantità' })
        .max(100, { message: 'Al massimo 100 righe' }),
});
/** Body of `PATCH /api/quotes/:id/sections/:sectionId`. */
export const renameSectionSchema = z.object({ name: sectionName });
/**
 * Body of `POST /api/quotes/:id/duplicate` (decision of 26/09/2026): copy the
 * revenue only or the costs too, and whether the copy stays linked to its origin.
 */
export const duplicateQuoteSchema = z.preprocess((value) => value ?? {}, z.object({
    withCosts: z.boolean().default(true),
    link: z.boolean().default(false),
    note: optionalText(200),
}));
/**
 * Query of `GET /api/quotes`: the quotes of one client, for its Documenti
 * section in the registry (decision of 26/09/2026). Always within the data
 * scope of the user.
 */
export const quoteListQuerySchema = z.object({
    clientId: idSchema.optional(),
});
/** Body of `POST /api/quotes/:id/links`: another quote of the same client, with a note. */
export const linkQuoteSchema = z.object({
    quoteId: idSchema,
    note: optionalText(200),
});
export const QUOTE_LINK_LABELS = {
    origin: 'Origine',
    copy: 'Duplicato da questo',
    'revision-of': 'Versione precedente',
    'replaced-by': 'Versione successiva',
    manual: 'Collegato',
};
/** Body of `PUT /api/quotes/:id/document-discount`: the taxable amount or the total wanted. */
export const documentDiscountSchema = z.object({
    kind: z.enum(DOCUMENT_DISCOUNT_KINDS),
    amount: money.refine((value) => Number(value) > 0, {
        message: 'L’importo deve essere maggiore di zero',
    }),
});
/** Body of `POST /api/quotes/:id/approve`: the client the offer goes to. */
export const approveQuoteSchema = z.object({ clientId: idSchema });
/** Body of `POST /api/quotes/:id/state`: where the offer goes by hand. */
export const quoteStateSchema = z.object({
    state: z.enum(OFFER_MOVE_STATES),
});
/** Body of `PUT /api/quotes/:id/owner`: the new commerciale di riferimento. */
export const changeOwnerSchema = z.object({ userId: z.string().uuid() });
const installmentSchema = z
    .object({
    description: z.string().trim().min(1, { message: 'Scrivi quando si fattura' }).max(200),
    percent: z
        .union([z.string(), z.number()])
        .nullish()
        .transform((value) => value === '' || value === undefined || value === null
        ? null
        : String(value).replace(',', '.'))
        .refine((value) => value === null || (Number(value) > 0 && Number(value) <= 100), {
        message: 'Una percentuale tra 0 e 100',
    }),
    amount: z
        .union([z.string(), z.number()])
        .nullish()
        .transform((value) => value === '' || value === undefined || value === null ? null : String(value))
        .refine((value) => value === null || parseDecimal(value) !== null, {
        message: 'Importo non valido',
    })
        .transform((value) => (value === null ? null : String(parseDecimal(value)))),
    termsCode: z
        .preprocess((value) => (value === '' ? null : value), entryCodeSchema.nullish())
        .transform((value) => value ?? null),
    notes: optionalText(300),
})
    .refine((item) => (item.percent === null) !== (item.amount === null), {
    message: 'Una percentuale oppure un importo',
    path: ['percent'],
});
/** Body of `PUT /api/quotes/:id/installments`: the whole plan, in its order. */
export const installmentsSchema = z.object({
    installments: z.array(installmentSchema).max(24, { message: 'Al massimo 24 rate' }),
});
/** The amount of each instalment and whether the plan adds up to the taxable amount. */
export function installmentAmounts(installments, net) {
    const amounts = installments.map((item) => item.amount !== null ? Number(item.amount) : Math.round(net * Number(item.percent ?? 0)) / 100);
    const total = Math.round(amounts.reduce((sum, value) => sum + value, 0) * 100) / 100;
    return { amounts, total, matches: installments.length === 0 || Math.abs(total - net) < 0.01 };
}
export const OFFER_LOG_KINDS = ['telefonata', 'email', 'incontro', 'nota'];
export const OFFER_LOG_LABELS = {
    telefonata: 'Telefonata',
    email: 'E-mail',
    incontro: 'Incontro',
    nota: 'Nota',
};
/** Body of `POST /api/quotes/:id/log` and `PATCH /api/quotes/:id/log/:entryId`. */
export const offerLogSchema = z.object({
    occurredAt: z.string().datetime({ offset: true, message: 'Data e ora non valide' }),
    kind: z.enum(OFFER_LOG_KINDS),
    contact: optionalText(200),
    text: z.string().trim().min(1, { message: 'Scrivi cosa è successo' }).max(4000),
});
/** Hours an entry of the log stays correctable by its author. */
export const OFFER_LOG_EDIT_HOURS = 24;
//# sourceMappingURL=quotes.js.map